import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiLoading } from '@mdi/js'
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { AppIcon } from '../ui/icon'
import {
    confirmMatches,
    typeLabel,
    useArchiveDeletePreview,
    useArchiveBatchPreview,
    useBatchDeleteArchived,
    useDeleteArchivedAsset,
    BATCH_CHUNK,
    type ArchiveBatchFilter,
    type ArchiveBatchResult,
    type ArchiveDeleteBlocker,
    type ArchivedAsset,
    type ArchivedKind,
} from '../../hooks/use-archive'
import type { Notify } from './ArchivedItemsPage'

function errorCode(err: unknown): string {
    const code = (err as { code?: string } | undefined)?.code
    return code ?? 'generic'
}

function BlockerList({ blockers }: { blockers: ArchiveDeleteBlocker[] }) {
    const { t } = useTranslation('archive')
    if (blockers.length === 0) return null
    return (
        <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive" data-testid="archive-delete-blockers">
            <div className="font-medium">{t('dialog.blockers_title')}</div>
            <ul className="list-disc pl-5">
                {blockers.map((b) => (
                    <li key={b.code} data-testid={`archive-blocker-${b.code}`}>
                        {t(`dialog.blocker_${b.code}`, { count: b.count, names: (b.names ?? []).join(', ') })}
                    </li>
                ))}
            </ul>
        </div>
    )
}

/**
 * Delete one archived subtree: the preview of what goes with it, its
 * blockers, and the asset name to type before the button enables.
 */
export function ArchiveDeleteDialog({ org, asset, notify, onClose }: { org: string; asset: ArchivedAsset; notify: Notify; onClose: () => void }) {
    const { t, i18n } = useTranslation('archive')
    const preview = useArchiveDeletePreview(org, asset.uid)
    const remove = useDeleteArchivedAsset(org)
    const [typed, setTyped] = useState('')
    const [error, setError] = useState<string | null>(null)
    const name = asset.name || asset.uid.slice(0, 8)
    const p = preview.data
    const blocked = (p?.blockers ?? []).length > 0
    const canConfirm = !!p && !blocked && confirmMatches(typed, asset.name, asset.uid) && !remove.isPending

    const confirm = async () => {
        if (!p) return
        setError(null)
        try {
            await remove.mutateAsync({ assetUid: asset.uid, confirm: typed, expectedDescendantCount: p.descendant_count })
            notify(t('deleted'))
            onClose()
        } catch (err) {
            const code = errorCode(err)
            setError(t(`errors.${code}`, { defaultValue: t('errors.generic') }))
            if (code === 'SUBTREE_CHANGED') void preview.refetch()
        }
    }

    const counts: [string, number | undefined][] = p
        ? [
              ['documents_deleted', p.documents_deleted],
              ['documents_kept', p.documents_kept],
              ['pictures', p.pictures],
              ['issues', p.issues],
              ['relations', p.relations],
              ['contracts', p.contracts],
              ['conversations', p.conversations],
              ['interventions', p.interventions],
          ]
        : []

    return (
        <AlertDialog open onOpenChange={(open) => !open && onClose()}>
            <AlertDialogContent data-testid="archive-delete-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('dialog.title', { name })}</AlertDialogTitle>
                    <AlertDialogDescription>{t('dialog.description')}</AlertDialogDescription>
                </AlertDialogHeader>

                {preview.isLoading && <div className="text-sm text-muted-foreground">{t('dialog.loading')}</div>}
                {preview.error && <div className="text-sm text-destructive">{t('errors.generic')}</div>}
                {p && (
                    <div className="space-y-3 text-sm" data-testid="archive-delete-preview">
                        <div className="font-medium" data-testid="archive-preview-descendants">
                            {t('dialog.descendants', { count: p.descendant_count })}
                        </div>
                        {p.assets_by_type.length > 0 && (
                            <div>
                                <div className="text-xs uppercase text-muted-foreground">{t('dialog.by_type')}</div>
                                <ul className="pl-1">
                                    {p.assets_by_type.map((row) => (
                                        <li key={row.type_code ?? '-'}>
                                            {typeLabel(row.type_name, row.type_code, i18n.language) || '—'}: {row.count}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
                            {counts.map(([key, value]) => (
                                <div key={key} className="contents" data-testid={`archive-preview-${key}`}>
                                    <dt className="text-muted-foreground">{t(`dialog.${key}`)}</dt>
                                    <dd className="text-right tabular-nums">{value ?? 0}</dd>
                                </div>
                            ))}
                        </dl>
                        <BlockerList blockers={p.blockers} />
                        {!blocked && (
                            <div className="space-y-1">
                                <Label htmlFor="archive-delete-confirm">{t('dialog.type_to_confirm', { name })}</Label>
                                <Input
                                    id="archive-delete-confirm"
                                    value={typed}
                                    autoComplete="off"
                                    onChange={(e) => setTyped(e.target.value)}
                                    data-testid="archive-delete-confirm-input"
                                />
                            </div>
                        )}
                    </div>
                )}
                {error && (
                    <div className="text-sm text-destructive" role="alert" data-testid="archive-delete-error">
                        {error}
                    </div>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="archive-delete-cancel" disabled={remove.isPending}>
                        {t('dialog.cancel')}
                    </AlertDialogCancel>
                    <Button variant="destructive" disabled={!canConfirm} onClick={confirm} data-testid="archive-delete-confirm">
                        {remove.isPending && <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />}
                        {t('dialog.confirm')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

export type BatchSelection = { uids: string[] } | { filter: ArchiveBatchFilter }

/**
 * Delete a batch of archived assets or documents (spec 2026-10-09-03). The
 * batch is resolved by the server first: the dialog shows the summed impact
 * and the blocked roots left out, asks for the count to be typed, then sends
 * the previewed uids in chunks of 200 and lists the result per item.
 */
export function ArchiveBulkDeleteDialog({
    org,
    kind,
    selection,
    notify,
    onClose,
}: {
    org: string
    kind: ArchivedKind
    selection: BatchSelection
    notify: Notify
    onClose: (done: boolean) => void
}) {
    const { t, i18n } = useTranslation('archive')
    const previewer = useArchiveBatchPreview(org)
    const batch = useBatchDeleteArchived(org)
    const [typed, setTyped] = useState('')
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
    const [results, setResults] = useState<ArchiveBatchResult[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const started = useRef(false)

    const { mutateAsync: loadPreview } = previewer
    useEffect(() => {
        if (started.current) return
        started.current = true
        void loadPreview({ kind, ...selection }).catch(() => setError(t('errors.generic')))
    }, [loadPreview, kind, selection, t])

    const p = previewer.data
    const count = p?.uids.length ?? 0
    const running = progress !== null && results === null
    const canConfirm = !!p && count > 0 && typed.trim() === String(count) && !running && results === null

    const confirm = async () => {
        if (!p) return
        setError(null)
        const all: ArchiveBatchResult[] = []
        setProgress({ done: 0, total: count })
        try {
            for (let i = 0; i < p.uids.length; i += BATCH_CHUNK) {
                const chunk = p.uids.slice(i, i + BATCH_CHUNK)
                const res = await batch.mutateAsync({ kind, uids: chunk, expected_count: chunk.length })
                all.push(...(res?.results ?? []))
                setProgress({ done: Math.min(i + BATCH_CHUNK, count), total: count })
            }
        } catch (err) {
            setError(t(`errors.${errorCode(err)}`, { defaultValue: t('errors.generic') }))
        }
        setResults(all)
        const deleted = all.filter((r) => r.deleted).length
        notify(t('bulk.result', { deleted, failed: all.length - deleted }), deleted === all.length ? 'ok' : 'error')
    }

    const failed = (results ?? []).filter((r) => !r.deleted)
    const counts: [string, number][] = p
        ? kind === 'assets'
            ? [
                  ['documents_deleted', p.documents_deleted],
                  ['documents_kept', p.documents_kept],
                  ['pictures', p.pictures],
                  ['issues', p.issues],
                  ['relations', p.relations],
                  ['contracts', p.contracts],
                  ['conversations', p.conversations],
                  ['interventions', p.interventions],
              ]
            : [
                  ['documents_deleted', p.documents_deleted],
                  ['links', p.links],
                  ['pictures', p.pictures],
              ]
        : []

    return (
        <AlertDialog open onOpenChange={(open) => !open && !running && onClose(results !== null)}>
            <AlertDialogContent data-testid="archive-bulk-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{t(`bulk.title_${kind}`, { count })}</AlertDialogTitle>
                    <AlertDialogDescription>{t(`bulk.description_${kind}`)}</AlertDialogDescription>
                </AlertDialogHeader>

                {!p && !error && <div className="text-sm text-muted-foreground">{t('dialog.loading')}</div>}
                {p && results === null && (
                    <div className="space-y-3 text-sm" data-testid="archive-bulk-preview">
                        <div className="font-medium" data-testid="archive-bulk-total">
                            {t('bulk.total', { count })}
                        </div>
                        {kind === 'assets' && (
                            <div data-testid="archive-bulk-descendants">{t('dialog.descendants', { count: p.descendants })}</div>
                        )}
                        {kind === 'assets' && p.assets_by_type.length > 0 && (
                            <ul className="pl-1">
                                {p.assets_by_type.map((row) => (
                                    <li key={row.type_code ?? '-'}>
                                        {typeLabel(row.type_name, row.type_code, i18n.language) || '—'}: {row.count}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
                            {counts.map(([key, value]) => (
                                <div key={key} className="contents" data-testid={`archive-bulk-${key}`}>
                                    <dt className="text-muted-foreground">{t(`dialog.${key}`)}</dt>
                                    <dd className="text-right tabular-nums">{value}</dd>
                                </div>
                            ))}
                        </dl>
                        {p.blocked.length > 0 && (
                            <div
                                className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-destructive"
                                data-testid="archive-bulk-blocked"
                            >
                                <div className="font-medium">{t('bulk.blocked_title', { count: p.blocked.length })}</div>
                                <ul className="max-h-32 list-disc overflow-auto pl-5">
                                    {p.blocked.map((b) => (
                                        <li key={b.uid}>
                                            {b.name || b.uid.slice(0, 8)}:{' '}
                                            {b.blockers.map((bl) => t(`dialog.blocker_${bl.code}`, { count: bl.count, names: (bl.names ?? []).join(', ') })).join(' ')}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {count > 0 && (
                            <div className="space-y-1">
                                <Label htmlFor="archive-bulk-confirm">{t('bulk.type_to_confirm', { count })}</Label>
                                <Input
                                    id="archive-bulk-confirm"
                                    value={typed}
                                    autoComplete="off"
                                    disabled={running}
                                    onChange={(e) => setTyped(e.target.value)}
                                    data-testid="archive-bulk-confirm-input"
                                />
                            </div>
                        )}
                        {progress && (
                            <div role="status" data-testid="archive-bulk-progress">
                                {t('bulk.progress', { done: progress.done, total: progress.total })}
                            </div>
                        )}
                    </div>
                )}
                {results !== null && (
                    <div className="space-y-2 text-sm" data-testid="archive-bulk-results">
                        <div className="font-medium" data-testid="archive-bulk-summary">
                            {t('bulk.result', { deleted: results.length - failed.length, failed: failed.length })}
                        </div>
                        {failed.length > 0 && (
                            <ul className="max-h-40 list-disc overflow-auto pl-5 text-destructive" data-testid="archive-bulk-failures">
                                {failed.map((r) => (
                                    <li key={r.uid ?? r.asset_uid}>
                                        {(r.uid ?? r.asset_uid ?? '').slice(0, 8)}: {t(`errors.${r.error ?? 'generic'}`, { defaultValue: t('errors.generic') })}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {error && (
                    <div className="text-sm text-destructive" role="alert" data-testid="archive-bulk-error">
                        {error}
                    </div>
                )}
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="archive-bulk-cancel" disabled={running} onClick={() => onClose(results !== null)}>
                        {results !== null ? t('bulk.close') : t('dialog.cancel')}
                    </AlertDialogCancel>
                    {results === null && (
                        <Button variant="destructive" disabled={!canConfirm} onClick={confirm} data-testid="archive-bulk-confirm">
                            {running && <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />}
                            {t('dialog.confirm')}
                        </Button>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
